import Tippy from '@tippyjs/react';
import { Colaboracao, FocoNoCliente, Inovacao, MandouBem, Resolutividade, Lideranca, Uau, Valeu } from 'components/icons';
import InputMask from 'react-input-mask';

import { format, differenceInDays } from 'date-fns';

import { UserAvatar } from 'components/user-avatar';
import { useAuth } from 'context/AuthContext';
import { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { BiInfoCircle } from 'react-icons/bi';
import { FaHeart } from 'react-icons/fa';
import { MdModeComment } from 'react-icons/md';
import { registerInteraction } from 'services/interactions.service';
import { useModal } from 'context/ModalContext';
import { getKPISentHistoryPag, getServerTime } from 'services/dashboard.service';
import Box from 'components/ui/box';
import ArticlePlaceholder from 'components/ui/article-placeholder';
import { FiEdit3 } from 'react-icons/fi';
import { getActionById, updateRewardBehavior } from 'services/actionlog.service';
import Button from 'components/ui/button';
import { TbSearch } from 'react-icons/tb';
import { convertEmojis, itemsPerPageExtrato } from 'utils/helpers';
import Humanograma from 'components/Humanograma';
import { BsArrowReturnLeft } from 'react-icons/bs';
import { TbMessageCancel } from 'react-icons/tb';
import ModalCancelRecognition from 'components/modal-cancel-recognition';

const iconSize = 16;
const Coins: any = {
  uau: (
    <div className="w-[16px]">
      <Uau size={iconSize} />
    </div>
  ),
  mandou: (
    <div className="w-[16px]">
      <MandouBem size={iconSize} />
    </div>
  ),
  valeu: (
    <div className="w-[16px]">
      <Valeu size={iconSize} />
    </div>
  ),
};

const CoinLabel: any = {
  uau: '#UAU',
  mandou: '#MANDOUBEM',
  valeu: '#VALEU',
};

const Behaviors: any = {
  colaboracao: (
    <div className="w-[16px]">
      <Colaboracao size={iconSize} />
    </div>
  ),
  inovacao: (
    <div className="w-[16px]">
      <Inovacao size={iconSize} />
    </div>
  ),
  foco: (
    <div className="w-[16px]">
      <FocoNoCliente size={iconSize} />
    </div>
  ),
  resolutividade: (
    <div className="w-[16px]">
      <Resolutividade size={iconSize} />
    </div>
  ),
  lideranca: (
    <div className="w-[16px]">
      <Lideranca size={iconSize} />
    </div>
  ),
};

const BehaviorLabel: any = {
  colaboracao: 'Colaboração',
  inovacao: 'Inovação',
  foco: 'Foco no Cliente',
  resolutividade: 'Protagonismo',
  lideranca: 'Liderança Integradora',
};

interface Props {
  activeModal: (data: { message: string; id: string }) => void;
  filter?: any;
  message?: { message: string; id: string } | null;
}

export default function Recognition(data: Props) {
  const { activeModal, message, filter } = data;
  const { user, currentCycle } = useAuth();
  const [list, setList] = useState<any[]>([]);
  const [time, setTime] = useState<number>();
  const [total, setTotal] = useState(0);
  const [allLoaded, setAllLoaded] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [cancelRecognitionId, setCancelRecognitionId] = useState<string | undefined>();

  const { showModal } = useModal();

  // Ref para controlar se o carregamento inicial já foi feito
  const initialLoadDone = useRef(false);
  // Ref para o input de busca
  const searchInputRef = useRef<any>(null);

  const [loadingList, setLoadingList] = useState(true);

  function hasLike(item: any) {
    if (item?.attributes?.status !== 'OK') {
      return false;
    }

    return !!item?.interaction.find((i: any) => i.player === user?._id && !!i.like);
  }

  function canEdit(item: any) {
    if (item?.attributes?.status !== 'OK') {
      return false;
    }

    if (item && time) {
      const differenceTime = differenceInDays(new Date(time), new Date(item.time));
      return differenceTime <= 10;
    }

    return false;
  }

  function getComment(item: any) {
    if (item?.attributes?.status !== 'OK') {
      return null;
    }

    const interaction = item.interaction?.find((e: any) => !!e.message?.trim().length);
    return interaction || null;
  }

  function getReturnComment(item: any) {
    if (
      (item?.attributes?.status !== 'devolvido' && item?.attributes?.status !== 'cancelado') ||
      !item?.returnRecognition?.message
    ) {
      return null;
    }

    return item?.returnRecognition?.message;
  }

  async function updateRecognition(data: { message: string; id: string }) {
    const resp = await getActionById(data.id);
    resp.attributes.message = data.message;

    await updateRewardBehavior(resp);

    const newList = [...list];
    const index = newList.findIndex(e => e._id === data.id);
    newList[index].attributes.message = data.message;

    setList(newList);
  }

  // Carrega o horário do servidor
  useEffect(() => {
    async function loadTime() {
      const res = await getServerTime();
      setTime(res);
    }

    if (user && currentCycle) {
      loadTime();
    }
  }, [currentCycle, user]);

  // Atualiza reconhecimento quando a prop message muda
  useEffect(() => {
    if (message?.message && message?.id) {
      updateRecognition(message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [message]);

  // Função para carregar dados - recebe parâmetros explícitos para evitar problemas de timing
  const fetchData = useCallback(
    async (searchTerm: string, loadMore: boolean, currentList: any[]) => {
      if (loadMore) {
        setLoadingMore(true);
      } else {
        setLoadingList(true);
      }

      try {
        // Monta o filtro com base nos parâmetros
        const filterPayload: any = {
          ...filter,
          cycleId: filter?.cycleId || currentCycle?._id,
        };

        if (searchTerm.trim()) {
          filterPayload.search = searchTerm.trim();
        }

        // Se for "carregar mais", busca o restante (10000 para pegar tudo)
        // Se for primeira carga, busca apenas itemsPerPageExtrato
        const rangeStart = loadMore ? currentList.length : 0;
        const rangeTotal = loadMore ? 10000 : itemsPerPageExtrato;

        const response = await getKPISentHistoryPag(
          user?._id,
          filterPayload,
          rangeStart,
          rangeTotal,
          currentCycle?.date_start,
          currentCycle?.date_finish,
        );

        if (loadMore) {
          // Concatena com a lista existente
          setList([...currentList, ...response.result]);
          setAllLoaded(true); // Após carregar mais, já carregou tudo
        } else {
          // Primeira carga ou nova busca: substitui a lista
          setList(response.result);
          // Se o total for maior que o que carregamos, há mais itens
          setAllLoaded(response.range.total <= itemsPerPageExtrato);
        }

        setTotal(response.range.total);
      } catch (error) {
        console.error('Erro ao carregar reconhecimentos enviados:', error);
      } finally {
        setLoadingList(false);
        setLoadingMore(false);
      }
    },
    [currentCycle?._id, currentCycle?.date_finish, currentCycle?.date_start, filter, user?._id],
  );

  // Carregamento inicial - só executa uma vez quando as dependências estão prontas
  useEffect(() => {
    if (user && currentCycle && filter?.cycleId && !initialLoadDone.current) {
      initialLoadDone.current = true;
      fetchData('', false, []);
    }
  }, [user, currentCycle, filter?.cycleId, fetchData]);

  // Handler para busca
  const onSearch = (e: FormEvent) => {
    e.preventDefault();

    const inputValue = searchInputRef.current?.value || '';
    setSearchValue(inputValue);
    setAllLoaded(false);

    // Chama fetchData com o valor atual do input
    fetchData(inputValue, false, []);

    // Remove o foco do input
    if (searchInputRef.current) {
      searchInputRef.current.blur();
    }
  };

  // Handler para carregar mais
  const handleLoadMore = () => {
    if (loadingMore || allLoaded) return;
    fetchData(searchValue, true, list);
  };

  // Handler para o callback do modal de cancelamento
  const handleCancelCallback = (action: string) => {
    if (action === 'remove') {
      // Reseta e recarrega
      setAllLoaded(false);
      setSearchValue('');
      if (searchInputRef.current) {
        searchInputRef.current.value = '';
      }
      fetchData('', false, []);
    } else if (action === 'cantCancel') {
      setList(prevstate =>
        prevstate.map(x => {
          const obj = { ...x };
          if (obj._id === cancelRecognitionId) {
            obj.canCancelRecognition = false;
          }
          return obj;
        }),
      );
    } else if (action === 'close') {
      setCancelRecognitionId(undefined);
    }
  };

  // Determina se deve mostrar o botão "Carregar mais"
  const showLoadMoreButton = !allLoaded && total > itemsPerPageExtrato && list.length < total;

  return (
    <>
      <div className="my-14">
        <div className="flex flex-col items-start justify-start lg:flex-row lg:justify-between lg:items-center mb-8">
          <h3 className="text-white font-bold text-xl mb-4 lg:mb-0">
            Reconhecimentos enviados
            <span className="text-xl lg:text-2xl font-bold text-yellow ">{total ? ` (${total})` : null}</span>
          </h3>
          <div className="flex flex-row items-center space-x-4">
            <form onSubmit={onSearch} className="pl-1 -ml-1 flex-1 flex space-x-5">
              <InputMask
                placeholder="Pesquise com nome, matrícula, prefixo ou grupo"
                alwaysShowMask={false}
                mask={''}
                ref={(ref: any) => (searchInputRef.current = ref)}
                inputMode="search"
                style={{ fontSize: 16 }}
                className="min-w-full lg:min-w-[416px] bg-maastricht-blue text-sm text-white font-thin flex-1 rounded-md py-2 px-4"
              />
              <button type="submit" aria-label="Pesquisar">
                <TbSearch size={28} className="cursor-pointer hover:ring-2 mr-1 rounded-full" />
              </button>
            </form>
          </div>
        </div>

        <Box className="relative bg-oxford-blue border-electric-blue lg:p-6 flex flex-col justify-center items-center border-opacity-100 bg-opacity-100">
          {loadingList && (
            <div className="space-y-2 w-full">
              <ArticlePlaceholder />
              <ArticlePlaceholder />
            </div>
          )}

          {!loadingList && (
            <div className="space-y-2 w-full">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {!!list?.length &&
                  list?.map((item: any) => (
                    <div
                      className={`bg-maastricht-blue-3 rounded-xl text-gray-300 space-y-2 py-6 px-6 lg:px-8 ${
                        item?.attributes?.status !== 'OK' ? 'bg-opacity-50' : ''
                      }`}
                      key={item?._id}
                    >
                      <div className="flex flex-col lg:flex-col items-start lg:items-start mb-5">
                        <div className="flex-1 flex flex-col-reverse lg:flex-row items-start space-x-2 leading-tight w-full">
                          <div className="flex flex-1 w-full items-start">
                            <div className="mr-2">
                              <UserAvatar data={item.target} />
                            </div>
                            <div className="flex-1 w-full block">
                              <Humanograma
                                data={{
                                  ...item,
                                  nome: item?.target?.name,
                                  funcao: item?.target?.funcao,
                                  _id: item?.attributes?.target,
                                  prefixo: item?.target?.prefixo,
                                  nome_dependencia: item?.target?.nome_dependencia,
                                }}
                              >
                                <div className="w-full block text-sm lg:text-base text-alice-blue font-thin">
                                  VOCÊ enviou para: <br />
                                  <span className="font-semibold">
                                    {item.target.nome_guerra || item.target.name.split(' ')[0]}
                                  </span>{' '}
                                  ({item.attributes.target})
                                  <BiInfoCircle className="inline relative -top-[2px] ml-1" />
                                </div>
                              </Humanograma>

                              <div className="block 2xl:flex flex-col 2xl:flex-row 2xl:items-center w-full space-y-2 2xl:space-y-0 2xl:space-x-2 mt-1">
                                <div className="inline-flex flex-row items-center space-x-2 px-3 py-1 text-yellow bg-oxford-blue rounded-full">
                                  {Coins[item.attributes.coin]} <span className="text-xs">{CoinLabel[item.attributes.coin]}</span>
                                </div>
                                <br className="block 2xl:hidden" />
                                <div className="inline-flex flex-row items-center space-x-2 px-3 py-1 text-yellow bg-oxford-blue rounded-full">
                                  {Behaviors[item.attributes.behavior]}{' '}
                                  <span className="text-xs">{BehaviorLabel[item.attributes.behavior]}</span>
                                </div>

                                {!!item.total && item?.attributes?.status === 'OK' ? (
                                  <>
                                    <br className="block 2xl:hidden" />
                                    <div className="inline-flex items-center space-x-2">
                                      <div
                                        className={`ring-white whitespace-nowrap 2xl:mt-0 flex-1 2xl:flex-none inline-flex flex-row items-center space-x-2 px-2 bg-oxford-blue py-1 text-electric-blue rounded-full`}
                                      >
                                        <span>total = {item.total} pontos</span>{' '}
                                      </div>
                                    </div>
                                  </>
                                ) : null}

                                {item?.attributes?.status === 'devolvido' ? (
                                  <>
                                    <br className="block 2xl:hidden" />
                                    <div className="inline-flex flex-row items-center space-x-2 px-3 py-1 text-[#ED2B48] bg-oxford-blue rounded-full">
                                      <BsArrowReturnLeft size={16} />
                                      <span className="text-xs">Reconhecimento devolvido</span>
                                    </div>
                                  </>
                                ) : null}

                                {item?.attributes?.status === 'cancelado' ? (
                                  <>
                                    <br className="block 2xl:hidden" />
                                    <div className="inline-flex flex-row items-center space-x-2 px-3 py-1 text-[#ED2B48] bg-oxford-blue rounded-full">
                                      <TbMessageCancel size={16} />
                                      <span className="text-xs">Reconhecimento cancelado</span>
                                    </div>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          <div className="text-gray-400 text-xs leading-none flex justify-end w-full lg:w-auto my-3 lg:my-0">
                            <span>{format(new Date(item.time), 'dd MMM yyyy | HH:mm')}</span>
                          </div>
                        </div>
                      </div>

                      <hr className="border-prussian-blue" />

                      {!!item.attributes.message && (
                        <>
                          <div className="p-2 flex-1 flex-col">
                            <div className="flex justify-between mb-3">
                              <p className="text-electric-blue font-thin">Reconhecimento enviado:</p>
                              {canEdit(item) && (
                                <button
                                  onClick={() => {
                                    activeModal({
                                      id: item._id,
                                      message: item.attributes.message,
                                    });
                                  }}
                                  className={`flex items-center cursor-pointer text-electric-blue`}
                                >
                                  <FiEdit3 size={20} className="mr-2" /> Editar
                                </button>
                              )}
                            </div>
                            <div
                              className="text-alice-blue break-words"
                              dangerouslySetInnerHTML={{
                                __html: convertEmojis(item.attributes.message),
                              }}
                            />
                          </div>

                          {!!getComment(item) && (
                            <div className="p-2 flex-1 flex-col">
                              <p className="text-electric-blue font-thin">Comentário:</p>
                              <div
                                className="text-alice-blue"
                                dangerouslySetInnerHTML={{
                                  __html: convertEmojis(getComment(item).message),
                                }}
                              ></div>
                            </div>
                          )}

                          {!!getReturnComment(item) && (
                            <div className="p-2 flex-1 flex-col">
                              <p className="text-electric-blue font-thin">
                                Motivo {item?.attributes?.status === 'cancelado' ? 'do cancelamento' : 'da devolução'}:
                              </p>
                              <div
                                className="text-alice-blue"
                                dangerouslySetInnerHTML={{
                                  __html: convertEmojis(getReturnComment(item)),
                                }}
                              ></div>
                            </div>
                          )}
                        </>
                      )}

                      {(!!hasLike(item) || !!item.canCancelRecognition) && (
                        <div className="flex justify-end space-x-6">
                          {!!item.canCancelRecognition && (
                            <button
                              className={`flex items-center uppercase text-[#ED2B48]`}
                              onClick={() => setCancelRecognitionId(item._id)}
                            >
                              <TbMessageCancel size={20} className="mr-2" />
                              Cancelar
                            </button>
                          )}

                          {!!hasLike(item) && (
                            <button className={`flex items-center uppercase cursor-default text-electric-blue`}>
                              <FaHeart size={20} className="mr-2" />
                              Curtido
                            </button>
                          )}
                        </div>
                      )}

                      {!!item.systemMessage && (
                        <>
                          <hr className="border-prussian-blue !mt-4" />
                          <div className="p-2 flex-1 flex-col">
                            <div
                              className="text-alice-blue text-sm font-light italic text-gray-400"
                              dangerouslySetInnerHTML={{
                                __html: item.systemMessage,
                              }}
                            ></div>
                          </div>
                        </>
                      )}
                    </div>
                  ))}

                {!list?.length && (
                  <>
                    <p className="text-white text-base text-center font-light leading-none block py-6">
                      Nenhum reconhecimento enviado até o momento.
                    </p>
                  </>
                )}
              </div>

              {showLoadMoreButton && (
                <div className="w-full flex justify-center">
                  <Button
                    disabled={loadingMore}
                    className="px-4 py-2 mx-auto"
                    type="button"
                    onClick={handleLoadMore}
                  >
                    {loadingMore ? 'Carregando...' : 'Carregar mais'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </Box>
      </div>

      {!!cancelRecognitionId && (
        <ModalCancelRecognition
          callback={handleCancelCallback}
          id={cancelRecognitionId}
          type="cancel"
        />
      )}
    </>
  );
}
